import React, { useState, useEffect } from 'react';
import { X, FileText, Clock, User, Edit, Save, XCircle, Trash2 } from 'lucide-react';
import { getInstructionHistory, getInstructionsForEmployee, updateProperty } from '../services/propertyApi';
import type { SpecialInstructionHistory } from '../services/propertyApi';
import { useI18n } from '../contexts/I18nContext';

interface SpecialInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
}

const SpecialInstructionsModal: React.FC<SpecialInstructionsModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyName
}) => {
  const { t } = useI18n();
  const [currentInstructions, setCurrentInstructions] = useState<string | null>(null);
  const [history, setHistory] = useState<SpecialInstructionHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [isEditing, setIsEditing] = useState(false);
  const [editedInstructions, setEditedInstructions] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && propertyId) {
      loadInstructions();
    }
  }, [isOpen, propertyId]);

  // Função para validar UUID
  const isValidUUID = (str: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const loadInstructions = async () => {
    setIsLoading(true);
    setError(null);
    
    // Validar UUID antes de fazer requisição
    if (!isValidUUID(propertyId)) {
      setError(t('specialInstructions.invalidIdDesc', { id: propertyId }));
      setIsLoading(false);
      setCurrentInstructions(null);
      setHistory([]);
      setIsEditing(false); // Desabilitar edição se ID inválido
      return;
    }
    
    try {
      // Carregar instruções atuais
      const currentData = await getInstructionsForEmployee(propertyId);
      const instructions = currentData.instructions?.instructions || currentData.property?.special_instructions || null;
      // Converter string vazia para null para que o botão deletar apareça corretamente
      const instructionsValue = (instructions && instructions.trim() !== '') ? instructions : null;
      setCurrentInstructions(instructionsValue);
      setEditedInstructions(instructionsValue || '');

      // Carregar histórico
      const historyData = await getInstructionHistory(propertyId);
      setHistory(historyData);
    } catch (error: any) {
      console.error('Erro ao carregar instruções:', error);
      const errorMessage = error.response?.data?.error || error.message || t('specialInstructions.error.loading');
      setError(errorMessage);
      setCurrentInstructions(null);
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    
    // Validar UUID antes de salvar
    if (!isValidUUID(propertyId)) {
      setError(t('specialInstructions.invalidIdDesc', { id: propertyId }));
      setIsSaving(false);
      return;
    }
    
    try {
      await updateProperty(propertyId, {
        special_instructions: editedInstructions
      });
      
      // Recarregar instruções após salvar
      await loadInstructions();
      setIsEditing(false);
    } catch (error: any) {
      console.error('Erro ao salvar instruções:', error);
      const errorMessage = error.response?.data?.error || error.message || t('specialInstructions.error.saving');
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedInstructions(currentInstructions || '');
    setIsEditing(false);
    setError(null);
  };

  const handleDelete = async () => {
    if (!window.confirm(t('specialInstructions.confirmDelete'))) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    
    // Validar UUID antes de deletar
    if (!isValidUUID(propertyId)) {
      setError(t('specialInstructions.invalidIdDesc', { id: propertyId }));
      setIsDeleting(false);
      return;
    }
    
    try {
      // Atualizar com string vazia para deletar
      await updateProperty(propertyId, {
        special_instructions: ''
      });
      
      // Recarregar instruções após deletar
      await loadInstructions();
      setIsEditing(false);
    } catch (error: any) {
      console.error('Erro ao deletar instruções:', error);
      const errorMessage = error.response?.data?.error || error.message || t('specialInstructions.error.deleting');
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText size={24} style={{ color: '#6366f1' }} />
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {t('specialInstructions.title')}
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                {propertyName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 20px'
        }}>
          <button
            onClick={() => setActiveTab('current')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'current' ? '2px solid #6366f1' : '2px solid transparent',
              color: activeTab === 'current' ? '#6366f1' : '#6b7280',
              fontWeight: activeTab === 'current' ? '600' : '400',
              fontSize: '14px'
            }}
          >
            {t('specialInstructions.current')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'history' ? '2px solid #6366f1' : '2px solid transparent',
              color: activeTab === 'history' ? '#6366f1' : '#6b7280',
              fontWeight: activeTab === 'history' ? '600' : '400',
              fontSize: '14px'
            }}
          >
            {t('specialInstructions.history')} ({history.length})
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {t('loading')}
            </div>
          ) : activeTab === 'current' ? (
            <div>
              {error && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#991b1b',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}
              
              {!isValidUUID(propertyId) && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  color: '#92400e',
                  marginBottom: '16px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    ⚠️ {t('specialInstructions.invalidId')}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    {t('specialInstructions.invalidIdDesc', { id: propertyId })}
                  </div>
                  <div style={{ fontSize: '13px', color: '#78350f' }}>
                    {t('specialInstructions.invalidIdSolution')}
                  </div>
                </div>
              )}
              
              {isEditing ? (
                <div>
                  <textarea
                    value={editedInstructions}
                    onChange={(e) => setEditedInstructions(e.target.value)}
                    placeholder={t('specialInstructions.placeholder')}
                    style={{
                      width: '100%',
                      minHeight: '200px',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '15px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      lineHeight: '1.6'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleCancel}
                      disabled={isSaving}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <XCircle size={16} />
                      {t('actions.cancel')}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#6366f1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: isSaving ? 0.6 : 1
                      }}
                    >
                      <Save size={16} />
                      {isSaving ? t('specialInstructions.saving') : t('actions.save')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {isValidUUID(propertyId) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                      {currentInstructions && (
                        <button
                          onClick={handleDelete}
                          disabled={isDeleting}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.2s',
                            opacity: isDeleting ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => !isDeleting && (e.currentTarget.style.backgroundColor = '#dc2626')}
                          onMouseLeave={(e) => !isDeleting && (e.currentTarget.style.backgroundColor = '#ef4444')}
                        >
                          <Trash2 size={16} />
                          {isDeleting ? t('specialInstructions.deleting') : t('actions.delete')}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setIsEditing(true);
                          setEditedInstructions(currentInstructions || '');
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#6366f1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6366f1'}
                      >
                        <Edit size={16} />
                        {currentInstructions ? t('specialInstructions.edit') : t('specialInstructions.add')}
                      </button>
                    </div>
                  )}
                  
                  {currentInstructions ? (
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#374151',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {currentInstructions}
                      </p>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6b7280'
                    }}>
                      <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                      <p>{t('specialInstructions.none')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              {history.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {history.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: '16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <p style={{
                        margin: '0 0 12px 0',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#374151',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {item.instructions}
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '12px',
                        color: '#6b7280',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={14} />
                          <span>{formatDate(item.changed_at)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <User size={14} />
                          <span>{t('specialInstructions.changedBy')} {item.changed_by.substring(0, 8)}...</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p>{t('specialInstructions.noHistory')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecialInstructionsModal;
